import { useEffect, useState } from 'react';
import { Calendar, Download, RefreshCw, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import ServiceOverviewStats from '../../components/admin/ServiceOverviewStats';
import LLMStats from '../../components/admin/LLMStats';
import TTSStats from '../../components/admin/TTSStats';
import STTStats from '../../components/admin/STTStats';
import CostAnalysis from '../../components/admin/CostAnalysis';
import ProviderComparison from '../../components/admin/ProviderComparison';

const ServiceAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Обзор' },
    { id: 'llm', label: 'LLM' },
    { id: 'tts', label: 'TTS' },
    { id: 'stt', label: 'STT' },
    { id: 'costs', label: 'Затраты' },
    { id: 'comparison', label: 'Сравнение' },
  ];

  useEffect(() => {
    loadStats();
  }, [dateRange, activeTab]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);

      const url = `/api/admin/analytics/services/${activeTab}?${params}`;
      console.log('[ServiceAnalytics] Loading stats from:', url);

      const response = await api.get(url);
      console.log('[ServiceAnalytics] Response data:', response.data);

      setStats(response.data);
    } catch (error) {
      console.error('[ServiceAnalytics] Error loading service analytics:', error);
      console.error('[ServiceAnalytics] Error details:', error.response?.data);
      setError('Ошибка загрузки данных');
      toast.error('Не удалось загрузить статистику сервисов');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadStats();
    setRefreshing(false);
    toast.success('Данные обновлены');
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      params.append('type', activeTab);
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);
      
      const response = await api.get(`/api/admin/analytics/services/export?${params}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `service_analytics_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Данные экспортированы');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Ошибка экспорта данных');
    }
  };

  const handleDateRangeChange = (type, value) => {
    setDateRange(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (error && !stats) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ошибка загрузки</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Статистика AI сервисов</h1>
            <p className="text-gray-600 mt-1">Анализ использования и затрат AI сервисов</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Обновить
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Экспорт
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="flex items-center gap-4 bg-white p-4 rounded-lg border border-gray-200">
          <Calendar className="w-5 h-5 text-gray-500" />
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Период:</label>
            <input
              type="date"
              value={dateRange.start || ''}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Начальная дата"
            />
            <span className="text-gray-500">—</span>
            <input
              type="date"
              value={dateRange.end || ''}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Конечная дата"
            />
            {(dateRange.start || dateRange.end) && (
              <button
                onClick={() => setDateRange({ start: null, end: null })}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Сбросить
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-screen">
        {activeTab === 'overview' && (
          <ServiceOverviewStats stats={stats} loading={loading} />
        )}
        {activeTab === 'llm' && (
          <LLMStats stats={stats} loading={loading} />
        )}
        {activeTab === 'tts' && (
          <TTSStats stats={stats} loading={loading} />
        )}
        {activeTab === 'stt' && (
          <STTStats stats={stats} loading={loading} />
        )}
        {activeTab === 'costs' && (
          <CostAnalysis stats={stats} loading={loading} />
        )}
        {activeTab === 'comparison' && (
          <ProviderComparison stats={stats} loading={loading} />
        )}
      </div>
    </div>
  );
};

export default ServiceAnalytics;